import { useCallback, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { activateFlightPath, setActiveImageId } from "features/map/layers/slice"
import { stagedFiles } from "@nova/slice-multifile-upload"
import { removeLocalUsage } from "features/data-management/workspace/subscriptions/module"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"

export const useRemoveSelectedUploads = () => {
    const { flightId, uploads, selectFlightById, deleteUploadedUpload, deleteUpload } =
        useUploadFlowContext()
    const dispatch = useAppDispatch()
    const flight = useAppSelector(selectFlightById(flightId))

    const [isRemoving, setIsRemoving] = useState(false)

    const removeSelected = useCallback(
        async (selectedUploadIds: Record<string, boolean>) => {
            setIsRemoving(true)
            const remainingUploads = uploads.filter((upload) => !selectedUploadIds[upload.id])
            const selectedUploads = uploads.filter((upload) => selectedUploadIds[upload.id])

            //we do this promise and timeout logic to make sure we can set the loading states for isRemoving before the main thread gets blocked, and it keeps it awaitable
            await new Promise<void>((resolve) => {
                setTimeout(async () => {
                    await Promise.all(
                        selectedUploads.map(async (upload) => {
                            const size = stagedFiles[upload.id]?.size
                            removeLocalUsage(flightId, size)

                            // del() matches ids within this flight's images array, so ask that same
                            // collection instead of inferring from upload state.
                            const uploadExistsInProtobuf = !!flight?.images?.some(
                                (image: { id: string }) => image.id === upload.id
                            )

                            if (uploadExistsInProtobuf && deleteUploadedUpload) {
                                await dispatch(
                                    deleteUploadedUpload({ context: { flightId }, id: upload.id })
                                )
                            } else {
                                await dispatch(
                                    deleteUpload({
                                        setId: flightId,
                                        uploadId: upload.id,
                                    })
                                )
                            }
                            // Only once the delete succeeded — a retry needs this file back.
                            delete stagedFiles[upload.id]
                        })
                    )
                    dispatch(setActiveImageId())
                    dispatch(
                        activateFlightPath({
                            flightId,
                            uploads: remainingUploads,
                            autoZoom: true,
                            useExistingPath: false,
                        })
                    )
                    setIsRemoving(false)
                    resolve() // resolve after all async work inside timeout
                }, 0)
            })
        },
        [uploads, deleteUpload, deleteUploadedUpload, dispatch, flightId, flight]
    )

    return { removeSelected, isRemoving }
}
